<template>
	<v-card flat>
		<v-card-title>
			<div>
				<div class="title text-xs-left">Forms</div>
				<div class="caption text--secondary">View assigned forms and create a new assignment for this employee.
				</div>
			</div>
			<v-spacer></v-spacer>
			<v-chip small label class="mr-2">{{ outstandingFormsCount }} require action</v-chip>
			<v-chip v-if="submittedFormsCount > 0" small label color="success" text-color="white" class="mr-2">{{ submittedFormsCount }} submitted</v-chip>
			<v-chip v-if="cancelledFormsCount > 0" small label color="error" text-color="white" class="mr-3">{{ cancelledFormsCount }} cancelled</v-chip>
			<v-btn v-if="canAssign" color="primary" @click="onAssignClick">Assign Form</v-btn>
		</v-card-title>

		<v-card-text v-if="forms.length === 0 && !isLoading" class="text-xs-center py-5">
			<div class="subheading mb-2">No forms assigned yet</div>
			<div class="text--secondary mb-4">Assign a form to {{ employeeFirstName }} to start tracking it here.</div>
			<v-btn v-if="canAssign" color="primary" @click="onAssignClick">Assign First Form</v-btn>
		</v-card-text>

		<v-card-text v-else>
			<v-data-table disable-initial-sort :headers="headers" :items="forms" :loading="isLoading" :rows-per-page-items="rowsPerPageItems">
				<template slot="items" slot-scope="props">
					<tr>
						<td class="text-xs-left">
							<a href="" class="font-weight-medium" @click.prevent="openSubmission(props.item)">{{ props.item.formName }}</a>
						</td>
						<td class="text-xs-left">
							<v-chip small label :dark="getStatusChipColor(props.item.state) !== ''" :color="getStatusChipColor(props.item.state)">{{ getStatusFriendly(props.item.state) }}</v-chip>
						</td>
						<td class="text-xs-left"><AppDate :date="props.item.createdDateUtc" /></td>
						<td class="text-xs-left">
							<span v-if="props.item.submittedDateUtc"><AppDate :date="props.item.submittedDateUtc" /></span>
							<span v-else class="text--secondary">Not submitted</span>
						</td>
						<td class="text-xs-right">
							<v-btn v-if="canCompleteForm(props.item)" small color="primary" @click="openSubmission(props.item)">Complete Form</v-btn>
							<v-btn v-else-if="isSubmittedForm(props.item)" small flat @click="openSubmission(props.item)">View Submission</v-btn>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card-text>

		<v-dialog v-model="showAssignDialog" persistent max-width="640px">
			<v-card>
				<v-toolbar flat dense>
					<v-toolbar-title class="subheading">Assign Form to {{ employeeName }}</v-toolbar-title>
				</v-toolbar>

				<v-card-text>
					<v-autocomplete v-model="selectedFormId" :items="availableForms" item-text="name" item-value="id" label="Form" placeholder="Select a form" :return-object="false" :error-messages="formErrors" persistent-hint hint="This creates a form assignment for this employee." />
				</v-card-text>

				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn flat @click="closeDialog">Cancel</v-btn>
					<v-btn color="primary" :loading="isAssigning" :disabled="isAssigning || !selectedFormId" @click="submitAssign">Assign</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
export default {
	name: 'EmployeeAssignedFormsList',
	props: {
		forms: {
			type: Array,
			default: () => []
		},
		availableForms: {
			type: Array,
			default: () => []
		},
		isLoading: {
			type: Boolean,
			default: false
		},
		isAssigning: {
			type: Boolean,
			default: false
		},
		canAssign: {
			type: Boolean,
			default: false
		},
		employeeName: {
			type: String,
			default: 'this employee'
		},
		employeeFirstName: {
			type: String,
			default: 'this employee'
		},
		tenantId: {
			type: String,
			required: true
		},
		isEmployeeRespondent: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			showAssignDialog: false,
			selectedFormId: null,
			rowsPerPageItems: [10, 25, 50, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }]
		};
	},
	computed: {
		headers() {
			return [
				{ text: 'Name', value: 'formName' },
				{ text: 'Status', value: 'state' },
				{ text: 'Assigned', value: 'createdDateUtc' },
				{ text: 'Submitted', value: 'submittedDateUtc' },
				{ text: '', value: 'actions', sortable: false, align: 'right' }
			];
		},
		selectedForm() {
			return this.availableForms.find(form => form.id === this.selectedFormId) || null;
		},
		outstandingFormsCount() {
			return this.forms.filter(this.isOutstandingForm).length;
		},
		submittedFormsCount() {
			return this.forms.filter(this.isSubmittedForm).length;
		},
		cancelledFormsCount() {
			return this.forms.filter(form => form.state === 'Cancelled').length;
		},
		formErrors() {
			return this.selectedFormId ? [] : [];
		}
	},
	methods: {
		isSubmittedForm(item) {
			return item.state === 'Submitted' || !!item.submittedDateUtc;
		},
		isOutstandingForm(item) {
			return !this.isSubmittedForm(item) && item.state !== 'Cancelled';
		},
		getStatusFriendly(state) {
			switch (state) {
				case 'NotSubmitted':
					return 'Assigned';
				case 'Draft':
					return 'In Progress';
				case 'Submitted':
					return 'Submitted';
				case 'Cancelled':
					return 'Cancelled';
				default:
					return state || '';
			}
		},
		getStatusChipColor(state) {
			switch (state) {
				case 'Submitted':
					return 'success';
				case 'Draft':
					return 'warning';
				case 'Cancelled':
					return 'error';
				case 'NotSubmitted':
				default:
					return '';
			}
		},
		canCompleteForm(item) {
			const actionableStates = ['NotSubmitted', 'Draft'];
			return this.isEmployeeRespondent && actionableStates.includes(item.state) && !item.submittedDateUtc;
		},
		openSubmission(item) {
			this.$emit('openSubmission', {
				submissionId: item.submissionId,
				isReadonly: !this.canCompleteForm(item)
			});
		},
		onAssignClick() {
			this.$emit('openAssign');
			this.showAssignDialog = true;
		},
		closeDialog() {
			this.showAssignDialog = false;
			this.selectedFormId = null;
		},
		submitAssign() {
			if (!this.selectedFormId) return;
			this.$emit('assign', this.selectedFormId);
		}
	}
};
</script>
